import { Collapse, Stack, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useEffect, useRef, useState } from "react";

const useClampedTextReadMoreStyles = makeStyles({
  textClamped: {
    display: "-webkit-box",
    overflow: "hidden",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    wordBreak: "break-word",
  },
  textExpanded: {
    wordBreak: "break-word",
    whiteSpace: "pre-wrap",
  },
  readMoreButton: {
    cursor: "pointer",
    background: "none",
    border: 0,
    padding: 0,
    alignSelf: "flex-start",
    "&:hover": {
      textDecoration: "underline",
    },
  },
});

type ClampedTextReadMoreProps = {
  text: string;
};

export function ClampedTextReadMore({ text }: ClampedTextReadMoreProps) {
  const classes = useClampedTextReadMoreStyles();
  const [expanded, setExpanded] = useState(false);
  const [showReadMore, setShowReadMore] = useState(false);
  const clampRef = useRef<HTMLParagraphElement | null>(null);

  useEffect(() => {
    setExpanded(false);
  }, [text]);

  useEffect(() => {
    const el = clampRef.current;
    if (!el || expanded) return;

    setShowReadMore(el.scrollHeight > el.clientHeight);
  }, [text, expanded]);

  return (
    <Stack gap={0.25}>
      {!expanded && (
        <Typography
          ref={clampRef}
          variant="body2"
          color="text.secondary"
          className={classes.textClamped}
        >
          {text}
        </Typography>
      )}
      <Collapse in={expanded}>
        <Typography
          variant="body2"
          color="text.secondary"
          className={classes.textExpanded}
        >
          {text}
        </Typography>
      </Collapse>
      {showReadMore && (
        <Typography
          component="button"
          type="button"
          variant="caption"
          color="primary"
          fontWeight={600}
          onClick={() => setExpanded((prev) => !prev)}
          aria-expanded={expanded}
          aria-label={expanded ? "Read less" : "Read more"}
          className={classes.readMoreButton}
        >
          {expanded ? "Read less" : "Read more"}
        </Typography>
      )}
    </Stack>
  );
}
